import { useState, useEffect } from 'react';
import { RefreshCw, Satellite, Droplets } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { getCelestialData } from '@/services/celestial-service';
import { useWeatherStore } from '@/stores/weather-store';
import { useActivePropertyStore } from '@/stores/active-property-store';
import { HuntRatingBar } from '@/components/common/HuntRatingBar';
import { HuntWindowDisplay } from '@/components/conditions/HuntWindowDisplay';
import { MoonPhaseDisplay } from '@/components/conditions/MoonPhaseDisplay';
import { PropertySelector } from '@/components/common/PropertySelector';
import { SimilarDayInsight } from '@/components/patterns/SimilarDayInsight';
import { DataCockpit } from '@/components/common/DataCockpit';
import { usePropertyInsights } from '@/stores/pattern-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { computeBlindFavorability } from '@/services/blind-favorability-service';
import { WindFavorabilitySection } from '@/components/conditions/WindFavorabilitySection';
import type { SimilarDayMatch } from '@/types/patterns';
import type { Property } from '@/types/property';
import type { WeatherConditionsDisplay } from '@/services/weather-service';
import type { ConditionsQualityResult } from '@/types/conditions';
import type { DataCockpitCell } from '@/types/data-cockpit';

// ---------------------------------------------------------------------------
// Your History section
// ---------------------------------------------------------------------------

interface YourHistorySectionProps {
  propertyId: string;
  propertyName: string;
}

function YourHistorySection({ propertyId, propertyName }: YourHistorySectionProps) {
  const { insights, dataQuality } = usePropertyInsights(propertyId, propertyName);

  if (dataQuality === 'insufficient' || dataQuality === null) return null;

  const topMatch = insights?.insights.find((i): i is SimilarDayMatch => i.type === 'similar_day');
  if (!topMatch) return null;

  return (
    <div
      className="mt-3 pt-3 border-t border-[#2d2a26]"
      data-testid="your-history-section"
    >
      <SimilarDayInsight match={topMatch} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRECIP_HEADER_THRESHOLD = 10;

// ---------------------------------------------------------------------------
// Helper: build vital duo string for collapsed header
// ---------------------------------------------------------------------------

function buildVitalDuo(
  weather: WeatherConditionsDisplay | null,
  quality: ConditionsQualityResult | null,
): string {
  if (!weather) return '—';
  const temp = `${Math.round(weather.temp)}°F`;
  const wind = `${weather.windDirection} ${Math.round(weather.windSpeed)} mph`;
  // Include score label if available (reserved for future use)
  void quality;
  return `${temp} · ${wind}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ConditionsWidgetProps {
  properties: Property[];
  /** ID of the user's primary property (renamed from defaultPropertyId in M-07). */
  primaryPropertyId: string | null;
}

export function ConditionsWidget({ properties, primaryPropertyId }: ConditionsWidgetProps) {
  const [selectedId, setSelectedId] = useState<string>(() => {
    if (primaryPropertyId) {
      const found = properties.find((p) => p.id === primaryPropertyId);
      if (found) return found.id;
    }
    return properties[0]?.id ?? '';
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const navigate = useNavigate();
  const { setActiveProperty } = useActivePropertyStore();

  const {
    fetchConditions,
    refreshConditions,
    getConditions,
    getConditionsQuality,
    isLoading,
    getError,
  } = useWeatherStore();

  const { waypoints, loadWaypoints } = useWaypointStore();

  // SAFETY: selectedId always matches a property when properties.length > 0;
  // when empty, property will be undefined and the early return below fires.
  const property = properties.find((p) => p.id === selectedId) ?? properties[0];

  // All hooks must be called unconditionally before any early return (Rules of Hooks).
  // Side-effects gate on property existence internally.
  useEffect(() => {
    if (!property) return;
    void fetchConditions(property.id, property.latitude, property.longitude);
  // Fetch when the selected property changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  useEffect(() => {
    if (!property) return;
    void loadWaypoints(property.id);
  // Load waypoints when property changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  function handlePropertySelect(id: string) {
    setSelectedId(id);
    setActiveProperty(id);
  }

  if (properties.length === 0) {
    return (
      <div
        className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-3"
        data-testid="conditions-widget-sensor-offline"
      >
        <p className="field-label">CONDITIONS</p>
        <div className="flex flex-col gap-2">
          <p
            className="field-label"
            style={{ color: 'var(--color-text-muted)' }}
          >
            SENSOR OFFLINE
          </p>
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Property required for environmental data.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/land/properties/new', { state: { fromOnboarding: true } })}
          className="self-start px-4 py-2 rounded-lg border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors"
        >
          Add Property
        </button>
      </div>
    );
  }

  const now = new Date();
  const celestial = getCelestialData(now, property.latitude, property.longitude);

  const sunriseISO = celestial.sun.sunrise.toISOString();
  const sunsetISO = celestial.sun.sunset.toISOString();
  const civilDawnISO = celestial.sun.civilDawn.toISOString();
  const civilDuskISO = celestial.sun.civilDusk.toISOString();
  const moonRiseISO = celestial.moon.rise?.toISOString() ?? null;
  const moonSetISO = celestial.moon.set?.toISOString() ?? null;

  const weather = getConditions(property.id);
  const conditionsQuality = getConditionsQuality(property.id);
  const weatherLoading = isLoading(property.id);
  const weatherError = getError(property.id);

  // Compute blind favorability using the service (pure function, no side effects)
  const propertyWaypoints = waypoints.filter((wp) => wp.propertyId === property.id);
  const blindFavorability = weather
    ? computeBlindFavorability(propertyWaypoints, weather.windDirection)
    : [];

  function handleRefresh() {
    void refreshConditions(property.id, property.latitude, property.longitude);
  }

  // Build cockpit cell values (offline-safe with "--" fallback)
  const tempPrimary = weather ? `${Math.round(weather.temp)}°F` : '--';
  const tempSecondary = weather
    ? `High ${Math.round(weather.tempToday ?? weather.temp)}°F`
    : undefined;
  const coldFrontActive = weather?.tempDeltaFavorable === true && (weather.tempDelta ?? 0) <= -10;

  // Precipitation line in the thermal cell — sits at the bottom of the badge slot
  // (below COLD FRONT badge when present, below all other content)
  const precipBadge =
    weather?.precipitationProbability !== undefined ? (
      <div
        style={{ display: 'flex', flexDirection: 'row', gap: 4, marginTop: 2 }}
        data-testid="conditions-precip-line"
      >
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: '#6e6a63',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          PRECIP
        </span>
        <span
          style={{ fontSize: 12, color: '#6e6a63', fontFamily: 'DM Sans, sans-serif' }}
        >
          {weather.precipitationProbability}%
          {weather.precipitationVolume !== undefined && weather.precipitationVolume > 0
            ? ` · ${weather.precipitationVolume.toFixed(1)} in`
            : null}
        </span>
      </div>
    ) : undefined;

  const windPrimary = weather ? `${Math.round(weather.windSpeed)} mph` : '--';
  const windSecondary = weather ? weather.windDirection : undefined;

  const pressurePrimary = weather ? `${weather.pressure.toFixed(2)}` : '--';
  const pressureSecondary = weather
    ? `${weather.pressureTrend ?? 'steady'} · ${weather.pressureInSweetSpot ? 'sweet spot' : 'outside range'}`
    : undefined;

  const vitalDuo = buildVitalDuo(weather, conditionsQuality);
  const qualityScore = conditionsQuality?.score ?? null;
  const qualityLabel = conditionsQuality?.label ?? null;

  const tempCellBadge =
    coldFrontActive || precipBadge !== undefined ? (
      <>
        {coldFrontActive && (
          <span
            className="ml-1.5 text-[10px] font-bold text-[#6ab0f5] align-middle"
            data-testid="cold-front-badge"
          >
            COLD FRONT
          </span>
        )}
        {precipBadge}
      </>
    ) : undefined;

  // Build DataCockpit cells
  const cockpitCells: DataCockpitCell[] = [
    {
      label: 'Temp',
      primary: tempPrimary,
      secondary: tempSecondary,
      testId: 'cockpit-cell-temp',
      badge: tempCellBadge,
    },
    {
      label: 'Wind',
      primary: windPrimary,
      secondary: windSecondary,
      testId: 'cockpit-cell-wind',
      badge: (
        <WindFavorabilitySection
          rows={blindFavorability}
          windLabel={weather ? `${weather.windDirection} ${Math.round(weather.windSpeed)} mph` : null}
        />
      ),
    },
    {
      label: 'Hunt Window',
      primary: '',
      testId: 'cockpit-cell-hunt-window',
      // HuntWindowDisplay is rendered as the badge slot since it has its own layout
      badge: (
        <HuntWindowDisplay
          civilDawn={civilDawnISO}
          sunrise={sunriseISO}
          sunset={sunsetISO}
          civilDusk={civilDuskISO}
          variant="grid-cell"
        />
      ),
    },
    {
      label: 'Pressure',
      primary: pressurePrimary,
      secondary: pressureSecondary,
      testId: 'cockpit-cell-pressure',
      badge: weather?.pressureInSweetSpot ? (
        <span
          className="ml-1.5 text-[10px] font-bold text-[#4a8c5c] align-middle"
          data-testid="pressure-sweetspot-indicator"
        >
          SWEET SPOT
        </span>
      ) : undefined,
    },
  ];

  return (
    <div
      className={`bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg ${isExpanded ? 'overflow-visible' : 'overflow-hidden'}`}
      data-testid="conditions-widget"
    >
      {/* ── Property selector + refresh row ─────────────────────────────── */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]" data-testid="conditions-property-name">
          {property.name}
        </p>
        <div className="flex items-center gap-2">
          {properties.length > 1 && (
            <PropertySelector
              properties={properties}
              selectedPropertyId={selectedId}
              onSelect={handlePropertySelect}
            />
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={weatherLoading}
            className="text-[#9a9590] hover:text-[#c8651a] transition-colors p-1 rounded disabled:opacity-40"
            aria-label="Refresh weather"
            data-testid="refresh-weather-button"
          >
            {weatherError && !weather ? (
              <Satellite size={14} />
            ) : (
              <RefreshCw size={14} />
            )}
          </button>
        </div>
      </div>

      {/* ── Collapsible header ───────────────────────────────────────────── */}
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-[#2a2724] transition-colors"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((v) => !v)}
        data-testid="conditions-header-toggle"
      >
        {/* Hunt Rating Bar */}
        <div className="shrink-0">
          {qualityScore !== null ? (
            <HuntRatingBar score={qualityScore} />
          ) : (
            <div
              className="flex gap-1"
              data-testid="hunt-rating-bar"
              data-quality="unknown"
            >
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-1.5 w-6 rounded-full bg-[#2d2a26]" />
              ))}
            </div>
          )}
        </div>

        {/* Quality label */}
        <span
          className="text-[11px] font-bold uppercase tracking-wider text-[#e8e5e0] shrink-0"
          data-testid="conditions-quality-label"
        >
          {qualityLabel ?? (weatherLoading ? '…' : '—')}
        </span>

        {/* Vital duo */}
        <span
          className="text-[11px] text-[#9a9590] truncate flex items-center gap-1"
          data-testid="conditions-vital-duo"
        >
          {vitalDuo}
          {weather &&
            weather.precipitationProbability !== undefined &&
            weather.precipitationProbability > PRECIP_HEADER_THRESHOLD && (
              <>
                <span aria-hidden="true">·</span>
                <Droplets size={14} style={{ color: '#9a9590' }} aria-hidden="true" />
                <span style={{ color: '#9a9590' }}>
                  {weather.precipitationProbability}%
                </span>
              </>
            )}
        </span>

        {/* Expand chevron */}
        <svg
          className={`ml-auto shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9a9590"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* ── Expanded body ───────────────────────────────────────────────── */}
      {isExpanded && (
        <div data-testid="conditions-expanded-body">
          {/* 2×2 DataCockpit grid */}
          <div data-testid="conditions-cockpit-grid">
            <DataCockpit cells={cockpitCells} />
          </div>

          {/* Moon phase line */}
          <div
            className="px-4 py-2.5 flex items-center gap-2 border-t border-[#2d2a26]"
            data-testid="moon-phase-line"
          >
            <MoonPhaseDisplay
              phase={celestial.moon.phase}
              illumination={celestial.moon.illumination}
              rise={moonRiseISO}
              set={moonSetISO}
              compact
            />
          </div>

          {/* Your History section */}
          <YourHistorySection propertyId={property.id} propertyName={property.name} />

          {/* Weather unavailable / skeleton states */}
          {weatherLoading && !weather && (
            <div
              className="px-4 py-3 flex flex-col gap-1.5 animate-pulse"
              data-testid="weather-skeleton"
            >
              <div className="h-3 bg-[#2d2a26] rounded w-28" />
              <div className="h-3 bg-[#2d2a26] rounded w-20" />
            </div>
          )}

          {!weatherLoading && !weather && (
            <p
              className="px-4 py-3 text-[12px] text-[#9a9590]"
              data-testid="weather-unavailable"
            >
              {weatherError ?? 'Weather unavailable — no connection'}
            </p>
          )}

          {/* Footer row: Updated timestamp (left) + 8-Day Forecast link (right) */}
          <div className="px-4 py-3 border-t border-[#2d2a26] flex items-center justify-between gap-2">
            {weather ? (
              <p
                className="text-[10px] text-[#9a9590]"
                data-testid="last-updated"
              >
                Updated {new Date(weather.lastUpdated).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </p>
            ) : (
              <span />
            )}
            <Link
              to={`/forecast?propertyId=${property.id}`}
              className="text-[11px] text-[#c8651a] hover:text-[#e08a3d] transition-colors"
              data-testid="forecast-link"
            >
              8-Day Forecast →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
